import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Tooltip } from "@mui/material";
import LendyIcon from "./lendy/LendyIcon";
import LoanDocs from "../models/loanDocsModel";
import {
  groupDocumentsByFolder,
  getDocumentTypeLabel,
} from "../utils/documentFolders";
import "./ChatWithDocsFlow.css";
import "./WindowControls.css";

/**
 * Classifies a document into a display type using its mime type first, then
 * falling back to the file extension in its name. Returns the variant that
 * selects which line-art icon to render.
 */
const getDocType = (doc) => {
  const mime = (doc.mimeType || "").toLowerCase();
  const ext = (doc.name || "").split(".").pop()?.toLowerCase() || "";

  const is = (mimeMatch, exts) =>
    (mimeMatch && mime.includes(mimeMatch)) || exts.includes(ext);

  if (is("pdf", ["pdf"])) {
    return "pdf";
  }
  if (is("image", ["png", "jpg", "jpeg", "gif", "webp", "heic", "bmp", "tiff", "svg"])) {
    return "img";
  }
  if (is("spreadsheet", ["xls", "xlsx"]) || is("excel", []) || is("csv", ["csv"])) {
    return "sheet";
  }
  if (is("word", ["doc", "docx"]) || is("wordprocessing", [])) {
    return "word";
  }
  if (is("presentation", ["ppt", "pptx"])) {
    return "slides";
  }
  if (is("text/plain", ["txt", "rtf", "md"])) {
    return "text";
  }

  return "generic";
};

// Line-art file glyphs drawn with `currentColor` so the neutral icon chip
// controls their color, matching the single-tone SVG style used by Attach files.
const DOC_ICON_PATHS = {
  // Document outline with a folded corner + text lines (pdf / word / text).
  document: (
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
      <path d="M8.5 13h7M8.5 16.5h7" />
    </>
  ),
  // Plain document outline with a folded corner (generic).
  generic: (
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
    </>
  ),
  // Framed picture with a horizon (images).
  img: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="8.5" cy="9.5" r="1.5" />
      <path d="M21 16l-5-5L5 20" />
    </>
  ),
  // Grid / table (spreadsheets).
  sheet: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 9.5h18M3 14.5h18M9 4v16M15 4v16" />
    </>
  ),
  // Presentation frame (slides).
  slides: (
    <>
      <rect x="3" y="4" width="18" height="13" rx="2" />
      <path d="M12 17v3M9 20h6" />
    </>
  ),
};

const VARIANT_TO_GLYPH = {
  pdf: "document",
  word: "document",
  text: "document",
  img: "img",
  sheet: "sheet",
  slides: "slides",
  generic: "generic",
};

const DocIcon = ({ doc }) => {
  const variant = getDocType(doc);
  const glyph = DOC_ICON_PATHS[VARIANT_TO_GLYPH[variant] || "generic"];

  return (
    <span className="cwd-doc-icon" aria-hidden="true">
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {glyph}
      </svg>
    </span>
  );
};

const getPreviewImageUrl = (doc) => {
  const candidate = doc.previewImageURL || doc.downloadURL;
  if (!candidate || typeof candidate !== "string") {
    return null;
  }

  try {
    const url = new URL(candidate);
    if (!["http:", "https:"].includes(url.protocol)) {
      return null;
    }

    if (doc.previewImageURL) {
      return candidate;
    }

    const imageExtensions = [".webp", ".png", ".jpg", ".jpeg"];
    const isDirectImage = imageExtensions.some((extension) =>
      url.pathname.toLowerCase().endsWith(extension),
    );

    return isDirectImage ? candidate : null;
  } catch {
    return null;
  }
};

const DocPreview = ({ doc }) => {
  const previewUrl = getPreviewImageUrl(doc);
  const [hasPreviewError, setHasPreviewError] = useState(false);

  if (!previewUrl || hasPreviewError) {
    return <DocIcon doc={doc} />;
  }

  return (
    <span className="cwd-doc-icon cwd-doc-preview" aria-hidden="true">
      <img
        src={previewUrl}
        alt=""
        loading="lazy"
        decoding="async"
        onError={() => setHasPreviewError(true)}
      />
    </span>
  );
};

/** Screen-reader state of a document row. */
const getDocRowState = ({ isProcessing, checked }) => {
  if (isProcessing) {
    return "not processed yet";
  }

  return checked ? "selected" : "not selected";
};

/**
 * Multi-step "Chat with your docs" flow.
 * Step 1 (loans): pick a loan (skipped when a loan is already in context).
 * Step 2 (documents): pick documents to bring into the chat context.
 */
const ChatWithDocsFlow = ({
  isOpen,
  uid,
  organizationId,
  initialLoanId,
  initialLoanName,
  onClose,
  onConfirm,
  overlayMode = false,
  preselectedDocIdsByLoan = {},
}) => {
  const [step, setStep] = useState("loans");
  // Read through a ref so a new map on each render does not reload documents.
  const preselectedDocIdsByLoanRef = useRef(preselectedDocIdsByLoan);
  preselectedDocIdsByLoanRef.current = preselectedDocIdsByLoan;
  const [selectedLoan, setSelectedLoan] = useState(null);

  const [loans, setLoans] = useState([]);
  const [loansLoading, setLoansLoading] = useState(false);
  const [loansError, setLoansError] = useState(null);
  const [loanSearch, setLoanSearch] = useState("");
  // Server-side search + cursor pagination: the backend does the searching and
  // paging, so any loan (not just the first page) can be found and loaded.
  // `loanCursor === null` means there are no more pages to fetch.
  const [loanCursor, setLoanCursor] = useState(null);
  const [loadingMoreLoans, setLoadingMoreLoans] = useState(false);

  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState(null);
  const [docSearch, setDocSearch] = useState("");
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  // Folder ids the user has collapsed. Default is empty (all expanded), so
  // documents are visible as soon as a loan is opened.
  const [collapsedFolders, setCollapsedFolders] = useState(() => new Set());

  // Guards against double-submitting. Once confirmed, the parent app shows a
  // single full-screen "Chat with your docs" loading animation that persists
  // through navigation, so this panel intentionally shows no loader of its own.
  const [submitting, setSubmitting] = useState(false);

  // Request-ordering guards. Each fetch bumps a monotonic id and aborts the
  // previous in-flight request, so a slow earlier response (e.g. loan A) can
  // never overwrite the results of a newer selection/search (e.g. loan B).
  const docsReqIdRef = useRef(0);
  const docsAbortRef = useRef(null);
  const loansReqIdRef = useRef(0);
  const loansAbortRef = useRef(null);
  const loanSearchRef = useRef("");

  // Accessibility refs: the overlay element (initial focus + focus trap target),
  // the element focused before opening (to restore on close), and a stable
  // handle to onClose so the a11y effect doesn't re-run on every parent render.
  const overlayRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const onCloseRef = useRef(onClose);
  // Same ref the parent Chat page uses so the client's "Switch to side view"
  // button can align itself to this close control while the picker is open.
  const closeButtonRef = useRef(null);
  const [overlayBrandStyle, setOverlayBrandStyle] = useState({});
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Reset folder collapse state every time the picker opens so folders always
  // start fully expanded regardless of what the user collapsed last time.
  useEffect(() => {
    if (isOpen) {
      setCollapsedFolders(new Set());
    }
  }, [isOpen]);

  // Report this picker's close-button position to the parent client so the
  // floating minimize control stays next to the X (same mechanism as Chat.js).
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const sendPosition = () => {
      if (!closeButtonRef.current) {
        return;
      }

      const rect = closeButtonRef.current.getBoundingClientRect();
      window?.parent?.postMessage(
        {
          type: "close-button-position",
          position: {
            top: rect.top,
            left: rect.left,
            right: rect.right,
            bottom: rect.bottom,
            width: rect.width,
            height: rect.height,
          },
        },
        "*",
      );
    };

    const handleCloseButtonPositionRequest = (event) => {
      if (event.data === "request-close-button-position") {
        sendPosition();
      }
    };

    const timeoutId = setTimeout(sendPosition, 100);
    window.addEventListener("resize", sendPosition);
    window.addEventListener("message", handleCloseButtonPositionRequest);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", sendPosition);
      window.removeEventListener("message", handleCloseButtonPositionRequest);
    };
  }, [isOpen, overlayMode, step]);

  useEffect(() => {
    if (!isOpen || typeof document === "undefined") {
      return;
    }

    const rootStyles = getComputedStyle(document.documentElement);
    const primaryColor = rootStyles.getPropertyValue("--primary-color").trim();
    const selectedColor = rootStyles.getPropertyValue("--selected-color").trim();
    const labelColor = rootStyles.getPropertyValue("--background-label-color").trim();
    const nextStyle = {};

    if (primaryColor) {
      nextStyle["--primary-color"] = primaryColor;
    }
    if (selectedColor) {
      nextStyle["--selected-color"] = selectedColor;
    }
    if (labelColor) {
      nextStyle["--background-label-color"] = labelColor;
    }

    setOverlayBrandStyle(nextStyle);
  }, [isOpen]);

  const loadDocuments = useCallback(
    async (loanId) => {
      docsReqIdRef.current += 1;
      const reqId = docsReqIdRef.current;
      if (docsAbortRef.current) {
        docsAbortRef.current.abort();
      }
      const controller = new AbortController();
      docsAbortRef.current = controller;

      setDocsLoading(true);
      setDocsError(null);
      setDocuments([]);
      setSelectedDocIds([]);
      try {
        const docs = await LoanDocs.getLoanDocuments(loanId, organizationId, {
          signal: controller.signal,
        });
        // Ignore a stale response that no longer matches the latest request.
        if (reqId !== docsReqIdRef.current) {
          return;
        }
        setDocuments(docs);
        // Documents already in the chat open ticked (DEV-6130).
        const preselectedIds = new Set(preselectedDocIdsByLoanRef.current[loanId] || []);
        setSelectedDocIds(
          docs.filter((doc) => doc.hasText && preselectedIds.has(doc.id)).map(({ id }) => id),
        );
      } catch (err) {
        if (controller.signal.aborted || reqId !== docsReqIdRef.current) {
          return;
        }
        console.error("[ChatWithDocs] documents fetch failed:", err);
        setDocsError("Failed to load documents. Please try again.");
      }
      if (reqId === docsReqIdRef.current) {
        setDocsLoading(false);
      }
    },
    [organizationId],
  );

  // Fetch the first page of loans for the given search term (server-side).
  const loadLoans = useCallback(
    async (searchTerm = "") => {
      loanSearchRef.current = searchTerm;
      loansReqIdRef.current += 1;
      const reqId = loansReqIdRef.current;
      if (loansAbortRef.current) {
        loansAbortRef.current.abort();
      }
      const controller = new AbortController();
      loansAbortRef.current = controller;

      setLoansLoading(true);
      setLoansError(null);
      setLoanCursor(null);
      try {
        const { loans: records, cursor } = await LoanDocs.listLoans(
          uid || "",
          organizationId,
          { search: searchTerm, signal: controller.signal },
        );
        // A newer search finished first; drop this stale result so, e.g.,
        // "Smith" can't replace the visible "Taylor" results.
        if (reqId !== loansReqIdRef.current) {
          return;
        }
        setLoans(records);
        setLoanCursor(cursor);
      } catch (err) {
        if (controller.signal.aborted || reqId !== loansReqIdRef.current) {
          return;
        }
        console.error("[ChatWithDocs] loans fetch failed:", err);
        setLoansError("Failed to load loans. Please try again.");
      }
      if (reqId === loansReqIdRef.current) {
        setLoansLoading(false);
      }
    },
    [uid, organizationId],
  );

  // Fetch the next page using the cursor and append it, preserving the active
  // search so "Load more" keeps paging through the same result set.
  const loadMoreLoans = useCallback(async () => {
    if (!loanCursor || loadingMoreLoans) {
      return;
    }
    // Snapshot the request generation so a "load more" that resolves after a
    // new search began is discarded rather than appended to the new results.
    const reqIdAtStart = loansReqIdRef.current;
    const searchTermAtStart = loanSearchRef.current;
    setLoadingMoreLoans(true);
    try {
      const { loans: moreLoans, cursor } = await LoanDocs.listLoans(
        uid || "",
        organizationId,
        { search: searchTermAtStart, cursor: loanCursor, pageSize: 5 },
      );
      if (
        reqIdAtStart !== loansReqIdRef.current ||
        searchTermAtStart !== loanSearchRef.current
      ) {
        return;
      }
      setLoans((prev) => [...prev, ...moreLoans]);
      setLoanCursor(cursor);
    } catch (err) {
      if (
        reqIdAtStart !== loansReqIdRef.current ||
        searchTermAtStart !== loanSearchRef.current
      ) {
        return;
      }
      console.error("[ChatWithDocs] load more loans failed:", err);
      setLoansError("Failed to load more loans. Please try again.");
    }
    if (
      reqIdAtStart === loansReqIdRef.current &&
      searchTermAtStart === loanSearchRef.current
    ) {
      setLoadingMoreLoans(false);
    }
  }, [loanCursor, loadingMoreLoans, uid, organizationId]);

  // Initialize the flow whenever it opens.
  useEffect(() => {
    if (!isOpen) return;

    setSubmitting(false);
    if (initialLoanId) {
      const loan = { id: initialLoanId, name: initialLoanName || "Current loan" };
      setSelectedLoan(loan);
      setStep("documents");
      loadDocuments(initialLoanId);

      return;
    }

    setSelectedLoan(null);
    setStep("loans");
    setLoanSearch("");
  }, [isOpen, initialLoanId, initialLoanName, loadDocuments]);

  const handleLoanSearchChange = (value) => {
    // Invalidate in-flight search and pagination immediately — before the
    // debounce starts — so their results cannot flash under the new query.
    loanSearchRef.current = value.trim();
    loansReqIdRef.current += 1;
    loansAbortRef.current?.abort();
    setLoadingMoreLoans(false);
    setLoanSearch(value);
  };

  // Drive loan loading from the search box: load immediately on open (empty
  // query) and debounce keystrokes so each search hits the backend once settled.
  useEffect(() => {
    if (!isOpen || step !== "loans") {
      return;
    }

    const query = loanSearch.trim();
    const handle = setTimeout(() => {
      loadLoans(query);
    }, query ? 350 : 0);

    return () => clearTimeout(handle);
  }, [isOpen, step, loanSearch, loadLoans]);

  // Full-screen dialog accessibility: remember the trigger, move focus into the
  // picker on open, close on Escape, and restore focus to the trigger on close.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    previouslyFocusedRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const focusFrame = requestAnimationFrame(() => {
      const overlay = overlayRef.current;
      if (!overlay) {
        return;
      }
      const firstFocusable = overlay.querySelector(
        'input, button, [href], [tabindex]:not([tabindex="-1"])',
      );
      (firstFocusable || overlay).focus();
    });

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onCloseRef.current?.();

        return;
      }
      if (event.key !== "Tab") {
        return;
      }

      const overlay = overlayRef.current;
      if (!overlay) {
        return;
      }
      const focusable = Array.from(
        overlay.querySelectorAll(
          'input:not([disabled]), button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusable.length === 0) {
        event.preventDefault();
        overlay.focus();

        return;
      }

      const firstFocusable = focusable[0];
      const lastFocusable = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      } else if (!event.shiftKey && document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleKeyDown);
      const previous = previouslyFocusedRef.current;
      if (previous && typeof previous.focus === "function") {
        previous.focus();
      }
    };
  }, [isOpen]);

  const canLoadMoreLoans = loanCursor !== null && !loansLoading;

  const filteredDocs = useMemo(() => {
    const q = docSearch.trim().toLowerCase();
    if (!q) return documents;
    return documents.filter((doc) => doc.name.toLowerCase().includes(q));
  }, [documents, docSearch]);

  // Documents still processing have no text for Ask Addy to read yet, so
  // they are shown but cannot be picked (DEV-6130).
  const selectableFilteredDocs = useMemo(
    () => filteredDocs.filter((doc) => doc.hasText),
    [filteredDocs],
  );

  const allFilteredSelected =
    selectableFilteredDocs.length > 0 &&
    selectableFilteredDocs.every((doc) => selectedDocIds.includes(doc.id));

  const isSearching = docSearch.trim().length > 0;

  const folderSections = useMemo(
    () =>
      groupDocumentsByFolder(filteredDocs, {
        isVaLoan: /va/i.test(selectedLoan?.type || ""),
      }),
    [filteredDocs, selectedLoan],
  );

  const toggleFolder = (folderId) => {
    setCollapsedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }

      return next;
    });
  };

  const handleSelectLoan = (loan) => {
    setSelectedLoan(loan);
    setStep("documents");
    setDocSearch("");
    loadDocuments(loan.id);
  };

  const toggleDoc = (docId) => {
    setSelectedDocIds((prev) =>
      prev.includes(docId)
        ? prev.filter((id) => id !== docId)
        : [...prev, docId],
    );
  };

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      const filteredIds = new Set(selectableFilteredDocs.map((d) => d.id));
      setSelectedDocIds((prev) => prev.filter((id) => !filteredIds.has(id)));

      return;
    }
    setSelectedDocIds((prev) => {
      const merged = new Set(prev);
      for (const doc of selectableFilteredDocs) {
        merged.add(doc.id);
      }

      return Array.from(merged);
    });
  };

  const handleBackFromDocs = () => {
    // If a loan was pre-selected from context, backing out closes the flow.
    if (initialLoanId) {
      onClose();

      return;
    }
    setStep("loans");
  };

  // A loan that already has documents in the chat can be confirmed with none
  // ticked, which takes its documents out of the chat.
  const currentLoanId = selectedLoan?.id || initialLoanId;
  const hasDocsInChat = (preselectedDocIdsByLoan[currentLoanId] || []).length > 0;
  // Never confirm a list that has not loaded: an empty pick would remove the
  // loan's documents from the chat.
  const canConfirm =
    !docsLoading && !docsError && (selectedDocIds.length > 0 || hasDocsInChat);

  /**
   * Hands the picked documents (with their text) to Ask Addy, which attaches
   * them to the current chat (DEV-6130).
   */
  const handleConfirm = () => {
    if (!canConfirm || submitting) return;

    setSubmitting(true);
    onConfirm({
      loanId: selectedLoan?.id || initialLoanId,
      loanName: selectedLoan?.name || initialLoanName || "loan",
      documentIds: selectedDocIds,
      documents: documents.filter((doc) => selectedDocIds.includes(doc.id)),
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="cwd-overlay"
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label="Chat with your loan docs"
      tabIndex={-1}
      style={overlayBrandStyle}
    >
      {/* Header */}
      <div className="cwd-header">
        <button
          type="button"
          className="cwd-back-btn"
          onClick={step === "documents" ? handleBackFromDocs : onClose}
          disabled={submitting}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 18l-6-6 6-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {step === "documents" && !initialLoanId ? "Loans" : "Back"}
        </button>
        <span className="cwd-header-title">Chat with your loan docs</span>
        <span className="cwd-header-spacer" />
      </div>

      {/* Closes the picker, in the sidebar's window-control spot and style (WindowControls). */}
      <div
        id="addy-chat-close-button"
        ref={closeButtonRef}
        className={overlayMode ? "window-controls window-controls--overlay" : "window-controls"}
      >
        <Tooltip title="Close" arrow>
          <span>
            <button type="button" className="window-control" onClick={onClose} disabled={submitting} aria-label="Close">
              <LendyIcon name="x" />
            </button>
          </span>
        </Tooltip>
      </div>

      {step === "loans" ? (
        <div className="cwd-body">
          <div className="cwd-intro">
            <div className="cwd-intro-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 7a2 2 0 012-2h3l2 2h7a2 2 0 012 2v7a2 2 0 01-2 2H6a2 2 0 01-2-2V7z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className="cwd-intro-title">Pick a loan to chat with</h2>
            <p className="cwd-intro-sub">
              Choose a loan file, then select the documents you want Addy to
              reference.
            </p>
          </div>

          <div className="cwd-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
              <path
                d="M20 20l-3-3"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <input
              type="text"
              placeholder="Search by borrower, address, or loan ID..."
              value={loanSearch}
              onChange={(e) => handleLoanSearchChange(e.target.value)}
            />
          </div>

          <div className="cwd-list cwd-list-loans">
            {loansLoading ? (
              <div className="cwd-status">Loading loans…</div>
            ) : loansError ? (
              <div className="cwd-status cwd-status-error">
                {loansError}
                <button
                  type="button"
                  className="cwd-retry"
                  onClick={() => loadLoans(loanSearch.trim())}
                >
                  Retry
                </button>
              </div>
            ) : loans.length === 0 ? (
              <div className="cwd-status">No loans found.</div>
            ) : (
              <>
                {loans.map((loan) => (
                <button
                  key={loan.id}
                  type="button"
                  className="cwd-loan-row"
                  onClick={() => handleSelectLoan(loan)}
                >
                  <span className="cwd-loan-body">
                    <span className="cwd-loan-row-top">
                      <span className="cwd-loan-name">{loan.name}</span>
                      {loan.number && (
                        <span className="cwd-loan-num">#{loan.number}</span>
                      )}
                    </span>
                    {loan.address && (
                      <span className="cwd-loan-address">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M12 21s-6-5.686-6-10a6 6 0 1112 0c0 4.314-6 10-6 10z"
                            stroke="currentColor"
                            strokeWidth="1.8"
                          />
                          <circle
                            cx="12"
                            cy="11"
                            r="2"
                            stroke="currentColor"
                            strokeWidth="1.8"
                          />
                        </svg>
                        {loan.address}
                      </span>
                    )}
                  </span>
                  <svg
                    className="cwd-loan-chevron"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M9 6l6 6-6 6"
                      style={{ stroke: "var(--text-disabled)" }}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                ))}
                {canLoadMoreLoans && (
                  <button
                    type="button"
                    className="cwd-load-more"
                    onClick={loadMoreLoans}
                    disabled={loadingMoreLoans}
                  >
                    {loadingMoreLoans ? "Loading…" : "Load more loans"}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="cwd-body">
          <div className="cwd-doc-toolbar">
            <div className="cwd-search cwd-search-inline">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                <path
                  d="M20 20l-3-3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <input
                type="text"
                placeholder="Search documents..."
                value={docSearch}
                onChange={(e) => setDocSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="cwd-selectall-btn"
              onClick={toggleSelectAll}
              disabled={filteredDocs.length === 0}
            >
              {allFilteredSelected ? "Deselect All" : "Select All"}
            </button>
          </div>

          <div className="cwd-doc-count">
            {selectedDocIds.length} of {documents.length} document
            {documents.length === 1 ? "" : "s"} selected
          </div>

          <div className="cwd-list">
            {docsLoading ? (
              <div className="cwd-status">Loading documents…</div>
            ) : docsError ? (
              <div className="cwd-status cwd-status-error">
                {docsError}
                <button
                  type="button"
                  className="cwd-retry"
                  onClick={() => selectedLoan && loadDocuments(selectedLoan.id)}
                >
                  Retry
                </button>
              </div>
            ) : filteredDocs.length === 0 ? (
              <div className="cwd-status">No documents found.</div>
            ) : (
              folderSections.map((folder) => {
                // While searching, force every folder open so matches are never
                // hidden behind a collapsed header.
                const collapsed =
                  !isSearching && collapsedFolders.has(folder.id);

                return (
                  <div className="cwd-folder" key={folder.id}>
                    <button
                      type="button"
                      className="cwd-folder-header"
                      onClick={() => toggleFolder(folder.id)}
                      aria-expanded={!collapsed}
                    >
                      <svg
                        className="cwd-folder-icon"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M3 7.5A2.5 2.5 0 015.5 5H9l2 2h7.5A2.5 2.5 0 0121 9.5v7a2.5 2.5 0 01-2.5 2.5h-13A2.5 2.5 0 013 16.5v-9z"
                          stroke="currentColor"
                          strokeWidth="1.7"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span className="cwd-folder-name">{folder.name}</span>
                      <span className="cwd-folder-count">{folder.docs.length}</span>
                      <svg
                        className={`cwd-folder-chevron ${collapsed ? "" : "cwd-folder-chevron-open"}`}
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M6 9l6 6 6-6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>

                    {!collapsed && (
                      <div className="cwd-folder-docs">
                        {folder.docs.map((doc) => {
                          const checked = selectedDocIds.includes(doc.id);
                          const typeLabel = getDocumentTypeLabel(doc);
                          const isProcessing = !doc.hasText;

                          return (
                            <button
                              key={doc.id}
                              type="button"
                              className={`cwd-doc-row ${checked ? "cwd-doc-row-active" : ""} ${
                                isProcessing ? "cwd-doc-row-disabled" : ""
                              }`}
                              onClick={() => toggleDoc(doc.id)}
                              disabled={isProcessing}
                              aria-pressed={checked}
                              aria-label={`${doc.name || "Document"} ${getDocRowState({
                                isProcessing,
                                checked,
                              })}`}
                            >
                              <span
                                className={`cwd-checkbox ${checked ? "cwd-checkbox-on" : ""}`}
                              >
                                {checked && (
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <path
                                      d="M5 12l5 5L20 6"
                                      stroke="#fff"
                                      strokeWidth="3"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                )}
                              </span>
                              <DocPreview doc={doc} />
                              <span className="cwd-doc-info">
                                <span className="cwd-doc-name">{doc.name}</span>
                                <span className="cwd-doc-type">
                                  {isProcessing ? "Not processed yet" : typeLabel || folder.name}
                                </span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <div className="cwd-footer">
            <button
              type="button"
              className="cwd-footer-back"
              onClick={handleBackFromDocs}
            >
              Back
            </button>
            <button
              type="button"
              className="cwd-footer-confirm"
              disabled={!canConfirm || submitting}
              onClick={handleConfirm}
            >
              Select Documents
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWithDocsFlow;
